import {
  Dates,
  BicHelper,
  Numeric,
  Barcodes,
  QrCodes,
  InvoiceListItem,
  Translations
} from "@salaxy/core";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";

/** Represents a mapped invoice list item. */
export type MappedInvoiceListItem = {
  /** The heading of the invoice */
  heading: string
  /** The IBAN of the receiver */
  iban: string
  /** The name of the receiver. */
  nameOfReceiver: string
  /** The due date of the invoice. */
  dueDate: string
  /** The label for the reference or message. */
  referenceOrMessageLabel: string
  /** The due date of the invoice. */
  referenceOrMessageValue: string
  /** The payment amount. */
  payment: number
  /** The total amount paid. */
  totalPaid: number
  /** Indicates if the total amount has been paid. */
  isTotalPaid: boolean
  /** The payable amount. */
  payable: number
  /** The status text of the invoice. */
  statusText: string
  /** The SVG representation of the QR code. */
  qrCodeSvg: string
  /** The barcode of the invoice. */
  barcode: string
  /** The SVG representation of the barcode. */
  barcodeSvg: string
  /** Indicates if the invoice has been paid. */
  isPaid: boolean
  /** Indicates if the invoice has been canceled. */
  isCanceled: boolean
  /** Indicates if the invoice is in preview mode. */
  isPreview: boolean
  /** The display date of the invoice. */
  displayDate: string
  /** The text for unpaid invoices. */
  unPaidText: string
  /** Indicates if the QR code is available. */
  qrCodeAvailable: boolean
  /** Indicates if the QR code is unavailable. */
  qrCodeUnavailable: boolean
  /** The end date of the invoice. */
  endAt: string
};
/**
 * Mapper for InvoiceList.
 */
export class InvoiceListMapper {
  /**
   * Loads report type specific HeaderFooterData.
   * @param businessObject - The array of invoice list items.
   * @param reportOptions The ReportOptions entity used to load the HeaderFooterData.
   * @param _lang gets translation with this lang, if null use current lang
   * @returns Returns HeaderFooterData.
   */
  public static getHeaderFooter(
    businessObject: InvoiceListItem[],
    reportOptions: ReportOptions,
    _lang: string = null
  ): HeaderFooterData {
    const headerFooter: HeaderFooterData = {
      logo: reportOptions?.logo,
      img2: reportOptions?.img2,
      title: businessObject[0].ownerInfo.avatar.displayName,
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(Dates.getToday()),
    };
    return headerFooter;
  }

  /**
   * Loads the invoice layout for pdf or html reports.
   * @param pdf Are we rendering a pdf report or not.
   * @returns Returns invoice layout.
   */
  public static loadReportLayout(pdf: boolean): ReportLayout {
    const reportLayout: ReportLayout = {
      isLayoutA4Portrait: true,
      isLayoutA4Landscape: false,
      layout: "a4Portrait",
      date: Dates.getFormattedDate(Dates.getToday()),
      isPreview: false,
      mode: pdf ? "pdf" : "htmlPage",
      isModeHtmlPage: !pdf,
      isModePdf: pdf,
    };
    return reportLayout;
  }

  /**
   * Retrieves a invoice list based on the provided business object.
   * @param businessObject - The array of invoice list items.
   * @param getDocumentAndSvgNode - A function that returns the document and SVG node.
   * @returns A promise that resolves to an array of mapped invoices.
   */
  public static async getInvoiceList(businessObject: InvoiceListItem[], getDocumentAndSvgNode: () => any): Promise<MappedInvoiceListItem[]> {
    const final: MappedInvoiceListItem[] = [];
      for (const item of businessObject) {
        const result: MappedInvoiceListItem = {
          heading: this.getHeadingText(item),
          iban: item.otherPartyInfo?.ibanNumber || "",
          nameOfReceiver: item.otherPartyInfo.avatar.displayName,
          dueDate: item.endAt,
          referenceOrMessageLabel: item.entityType === "net" || item.entityType === "gross" ? Translations.get("SALAXY.REPORTS.invoiceList.message") : Translations.get("SALAXY.REPORTS.invoiceList.reference"),
          referenceOrMessageValue: item.entityType === "net" || item.entityType === "gross" ? `${Translations.get(`SALAXY.ENUM.InvoiceType.${item.entityType}.label`)} ${Dates.getFormattedRange(item.data.periodStart, item.data.periodEnd)}` : item.reference,
          payment: item.payment,
          totalPaid: (item.data as any)?.totalPaid,
          isTotalPaid: (item.data as any)?.totalPaid && (item.data as any)?.totalPaid !== item.payment,
          payable: item.payment - (item.data as any)?.totalPaid,
          statusText: `${Translations.get(`SALAXY.ENUM.InvoiceStatus.${item.status}.label`)}`,
          isPaid: item.status === "paid",
          isCanceled: item.status === "canceled",
          isPreview: item.status === "preview",
          displayDate: Dates.getFormattedDate((item.data as any)?.statusAt || item.updatedAt),
          unPaidText: `${item.businessObjects.length - ((item.data as any)?.paidBusinessObjectsCount || 0)} ${Translations.get("SALAXY.REPORTS.invoiceList.unpaidNetSalaries")} <br/> ${Dates.getFormattedRange(item.data.periodStart, item.data.periodEnd)}`,
          qrCodeAvailable: false,
          qrCodeUnavailable: false,
          qrCodeSvg: await this.createQrCode(item),
          barcode: Barcodes.getValue(
            item.otherPartyInfo.ibanNumber || "",
            item.entityType === "net" ? "" : item.reference,
            Dates.asDate(item.data.dueDate),
            Numeric.round(this.getInvoicePayment(item) * 100, 0)),
          barcodeSvg: this.createBarCode(getDocumentAndSvgNode, item.otherPartyInfo.ibanNumber || "", item.reference || "", Dates.asDate(item.data.dueDate), Numeric.round(this.getInvoicePayment(item) * 100, 0)),
          endAt: item.endAt
        };
        result.qrCodeAvailable = result.qrCodeSvg && item.status != "paid" && item.status != "canceled";
        result.qrCodeUnavailable = result.isPaid || result.isCanceled ? false : !result.qrCodeSvg ? true : false;
        final.push(result);
      }
    return final;
  }

  /**
   * Loads InvoiceList specific ReportOptions
   * @returns ReportOptions for report
   */
  public static loadReportOptions(): ReportOptions {
    return {
      css: "",
      printBackground: true,
      margin: {
        top: 30,
        bottom: 20,
        left: 10,
        right: 10,
      },
      headerTemplate: `
        <div style="color:#808080; margin:10mm 10mm 0 10mm;font-family: Helvetica, Arial, sans-serif;">
          <table style="width: 100%; border-bottom: 0.75mm solid #000;">
            <tr align="center">
              <td style="font-weight: bold; text-align: center">
                <div style="font-size: 1.3em">{{ title }}</div>
                <div style="font-size: 1.1em;padding-bottom: 2mm">{{ subTitle }}</div>
              </td>
            </tr>
          </table>
        </div>
      `,
      footerTemplate:
        `
          <div style="margin:0mm 10mm 0 10mm; padding-top: 3mm; border-top: 0.75mm solid #000;font-family: Helvetica, Arial, sans-serif;">
            {{t "SALAXY.REPORTS.invoice.footer.html" date=( today) }}
            <div style="position: absolute;top: 3mm; right: 10mm">
              {{> pageNumber }} / {{> totalPages }}
            </div>
          </div>
        `,
      logo: "",
    };
  }

   /**
    * Calculates the invoice payment amount for a given invoice item.
    * If the invoice item is of type "tax" or "union" and the total paid amount
    * is different from the payment amount, it returns the rounded difference
    * between the payment amount and the total paid amount.
    * Otherwise, it returns the payment amount.
    * @param {InvoiceListItem} item - The invoice item to calculate the payment for.
    * @returns {number} - The calculated payment amount.
    */
   private static  getInvoicePayment(item: InvoiceListItem): number {
    if((item.entityType == "tax" || item.entityType == "union") && (item.data as any)?.totalPaid && ((item.data as any).totalPaid != item.payment)) {
      return Numeric.round(item.payment - (item.data as any).totalPaid);
    }
    return item.payment;
  }

  private static getHeadingText(item: InvoiceListItem): string {
    return `${item.otherPartyInfo.avatar.displayName}: 
      ${item.entityType === "net" ||
        item.entityType === "gross" ||
          item.entityType === "foreclosure" ||
          item.entityType === "verification" ?
          `${Translations.get(`SALAXY.ENUM.InvoiceType.${item.entityType}.label`)} ${Dates.getFormattedDate(item.logicalDate)}` :
          `${Translations.get(`SALAXY.ENUM.InvoiceType.${item.entityType}.label`)} ${Dates.getFormattedRange(item.data.periodStart, item.data.periodEnd)}`
      }
    `;
  }

  private static createBarCode(getDocumentAndSvgNode: () => any, iban: string, ref: string, dueDate: string, euroAmount: number): string {
    const regex = /^(?:RF\d+|\d+)$/i;
    // check if ref starts with rf (case sensitive) and then contains numbers or if ref contains only numbers
    if(!regex.test(ref)) {
      return "";
    }

    try {
      return Barcodes.createBarcode(getDocumentAndSvgNode, iban, ref, dueDate, euroAmount, {width: 1.5, height: 20, displayValue: true, fontSize: 15});
    } catch(e) {
      console.error(e);
      return "";
    }
  }

  private static async createQrCode(item: InvoiceListItem): Promise<string> {
    const regex = /^(?:RF\d+|\d+)$/i;
    // check if ref starts with rf (case sensitive) and then contains numbers or if ref contains only numbers
    if(!regex.test(item.reference)) {
      return "";
    }

    try {
      return await QrCodes.getValue(
        BicHelper.getBicForIban(item.otherPartyInfo.ibanNumber.replace(/\s+/g, "")),
        item.otherPartyInfo.avatar.displayName,
        item.otherPartyInfo?.ibanNumber,
        item.entityType === "net" ? null : item.reference,
        item.entityType === "net" ? `${Translations.get(`SALAXY.ENUM.InvoiceType.${item.entityType}.label`)} ${Dates.getFormattedRange(item.data.periodStart, item.data.periodEnd)}` : null,
        this.getInvoicePayment(item),
        item.endAt
      );
    } catch(e) {
      console.error(e);
      return "";
    }
  }

}